import { Avatar, Box, Button, Text, Title } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { CheckIcon, FloppyDiskIcon, PaperPlaneTiltIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useBusiness } from "../../pages/sales-invoice/new";
import { currencyFormat } from "../../utils";
import { dateFormat } from "../../utils/date";
import { computeInvoiceTotals } from "../../utils/invoice-totals";
import { renderBusinessName } from "../../utils/render-business-name";
import classes from "./invoice-summary.module.css";

type Props = {
  form: UseFormReturnType<any>;
  onSaveDraft: () => void;
  onSend: () => void;
  loading?: boolean;
  compact?: boolean;
};

export function InvoiceSummary({ form, onSaveDraft, onSend, loading, compact }: Props) {
  const { t } = useTranslation("invoice");
  const values = form.values;
  const { business } = useBusiness();

  const {
    subtotal,
    vatTotal: totalVat,
    taxTotal: totalTax,
    discountAmount,
    shippingAmount,
    grandTotal,
    lineCount: totalItemCount,
  } = computeInvoiceTotals(values);

  const buyerName = business ? renderBusinessName(business) : null;
  const initials = buyerName
    ? buyerName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w: string) => w[0]?.toUpperCase() ?? "")
        .join("")
    : "-";

  const checks = useMemo(
    () => [
      {
        key: "buyer",
        label: t("summary.checkBuyer"),
        done: !!values.receiverBusinessId,
      },
      {
        key: "account",
        label: t("summary.checkAccount"),
        done: !!values.senderAccId,
      },
      {
        key: "lines",
        label: t("summary.checkLines"),
        done: totalItemCount > 0,
      },
      {
        key: "files",
        label: t("summary.checkAttachments"),
        done: (values.attachments?.length ?? 0) > 0,
        optional: true,
      },
    ],
    [t, totalItemCount, values.attachments, values.receiverBusinessId, values.senderAccId],
  );

  const due =
    values.paymentDate != null
      ? dateFormat(
          typeof values.paymentDate === "string"
            ? values.paymentDate
            : new Date(values.paymentDate).toISOString(),
        )
      : "-";

  if (compact) {
    return (
      <>
        <Box className={classes.mobileTotal}>
          <Text fz="sm" c="dimmed">
            {t("summary.grandTotalPay")}
          </Text>
          <Text fw={650} className={classes.num}>
            {currencyFormat(grandTotal)}
          </Text>
          {/* BELOW the total on purpose: the VAT is contained in it, not added to it. */}
          {totalVat > 0 ? (
            <Text className={classes.vatBelow}>
              {t("summary.vatIncluded")}: {currencyFormat(totalVat, { withSymbol: false })}
            </Text>
          ) : null}
        </Box>
        <Box className={classes.mobileActions}>
          <Button variant="default" size="sm" loading={loading} onClick={onSaveDraft}>
            {t("action.saveDraft")}
          </Button>
          <Button color="brand" size="sm" loading={loading} onClick={onSend} leftSection={<PaperPlaneTiltIcon size={14} />}>
            {t("action.sendApprove")}
          </Button>
        </Box>
      </>
    );
  }

  return (
    <Box className={classes.card}>
      <Title order={2} className={classes.title}>
        {t("summary.title")}
      </Title>

      <Box className={classes.buyer}>
        <Avatar radius="md" size={34} color="brand" variant="light">
          {initials}
        </Avatar>
        <Box className={classes.buyerMeta}>
          <Text fw={600} fz="sm" lineClamp={1}>
            {buyerName || t("summary.buyerEmpty")}
          </Text>
          <Text className={classes.buyerSub} lineClamp={1}>
            <Text span className={classes.mono}>
              {business?.regNumber ?? "-"}
            </Text>
            <Text span>
              {" "}
              · {t("summary.dueShort")}{" "}
            </Text>
            <Text span className={classes.date}>
              {due}
            </Text>
          </Text>
        </Box>
      </Box>

      <Box className={classes.rows}>
        <Box className={classes.row}>
          <Text span className={classes.rowLabel}>
            {t("summary.subtotalWithCount", { count: totalItemCount })}
          </Text>
          <Text span className={classes.num}>
            {currencyFormat(subtotal, { withSymbol: false })}
          </Text>
        </Box>
        <Box className={classes.row}>
          <Text span className={classes.rowLabel}>
            {t("summary.discount")}
          </Text>
          <Text span className={classes.num} c={discountAmount > 0 ? undefined : "dimmed"}>
            {currencyFormat(discountAmount, { withSymbol: false })}
          </Text>
        </Box>
        <Box className={classes.row}>
          <Text span className={classes.rowLabel}>
            {t("summary.shipping")}
          </Text>
          <Text span className={classes.num} c={shippingAmount > 0 ? undefined : "dimmed"}>
            {currencyFormat(shippingAmount, { withSymbol: false })}
          </Text>
        </Box>
        {totalTax > 0 ? (
          <Box className={classes.row}>
            <Text span className={classes.rowLabel}>
              {t("summary.tax")}
            </Text>
            <Text span className={classes.num}>
              {currencyFormat(totalTax, { withSymbol: false })}
            </Text>
          </Box>
        ) : null}
      </Box>

      <Box className={classes.totalRow}>
        <Text fw={600}>{t("summary.grandTotalPay")}</Text>
        <Text className={classes.totalValue}>
          {currencyFormat(grandTotal, { withSymbol: false })}{" "}
          <Text span className={classes.currency}>
            ₮
          </Text>
        </Text>
      </Box>

      {/* BELOW the total on purpose: the VAT is contained in it, not added to it. */}
      {totalVat > 0 ? (
        <Text className={classes.vatBelow}>
          {t("summary.vatIncluded")}: {currencyFormat(totalVat, { withSymbol: false })}
        </Text>
      ) : null}

      <Box className={classes.checks}>
        {checks.map((c) => (
          <Box key={c.key} className={classes.check} data-done={c.done || undefined}>
            <Box component="span" className={classes.checkBox}>
              {c.done ? <CheckIcon size={10} weight="bold" /> : null}
            </Box>
            <Text span>
              {c.label}
              {c.optional ? ` (${t("summary.optional")})` : ""}
            </Text>
          </Box>
        ))}
      </Box>

      <Button
        className={classes.sendBtn}
        color="brand"
        fullWidth
        loading={loading}
        onClick={onSend}
        leftSection={<PaperPlaneTiltIcon size={16} />}
      >
        {t("action.sendApprove")}
      </Button>
      <Button
        variant="default"
        fullWidth
        loading={loading}
        onClick={onSaveDraft}
        leftSection={<FloppyDiskIcon size={16} />}
      >
        {t("action.saveDraft")}
      </Button>
      <Text className={classes.hint}>{t("summary.hint")}</Text>
    </Box>
  );
}
